import { inject, Injectable } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';

import { SkillsImportHttpClient } from '@core/tellent-skills-data';
import type { SkillsImport } from '@core/tellent-skills-types';

import { pollUntilTerminal } from './poll-until-terminal';
import {
  SkillsImportEvent,
  SkillsImportProgressMessage,
} from './skills-import-progress-track.service';

@Injectable({ providedIn: 'root' })
export class SkillsImportPollingTrackService {
  private readonly http = inject(SkillsImportHttpClient);

  public track(importId: number): Observable<SkillsImportProgressMessage> {
    return pollUntilTerminal<SkillsImport, SkillsImportProgressMessage>({
      fetch: () => this.http.getImport(importId),
      toMessage: data => this.toMessage(data),
      isTerminal: message => message.event === SkillsImportEvent.Done,
      eventKey: message => message.event,
      // Matches the WS SkillsImportProgressTrackService: Failed / Cancelled
      // arrive via observer.error(...), so the loading dialog reads the
      // reason from its error: handler.
      onDeadline: () =>
        throwError(() => ({
          event: SkillsImportEvent.Failed,
          reason: 'polling_timeout',
        })),
    });
  }

  private toMessage(data: SkillsImport): Observable<SkillsImportProgressMessage> {
    switch (data.status) {
      case 'uploaded':
      case 'processing':
        return of({ event: SkillsImportEvent.Processing });
      case 'completed':
        return of({ event: SkillsImportEvent.Done });
      case 'failed':
        return throwError(() => ({
          event: SkillsImportEvent.Failed,
          reason: data.errorMessage ?? 'unknown_error',
        }));
      case 'cancelled':
        return throwError(() => ({
          event: SkillsImportEvent.Cancelled,
          reason: data.errorMessage ?? 'cancelled',
        }));
      default:
        return of({ event: SkillsImportEvent.Processing });
    }
  }
}
